<template>
	<transition name="dialogue-fade"
		:duration="duration"
		@after-enter="ready"
		@before-leave="unready"
	>
		<div :class="['dialogue-bubble', position]" v-show="show" :style="durationVariables">
			<div :class="['dialogue-inner', { 'is-ready': isReady }]">
				<slot></slot>
				<div class="dialogue-footer">
					<slot name="footer"></slot>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import { isOneOf } from "@/lib/validators";
import { DIALOGUE_COMPONENT_POSITIONS } from "@/constants";

export default {
	name: 'DialogueBubble',
	props: {
		show: {
			type: Boolean,
			required: false,
			default: true,
		},
		duration: {
			type: Number,
			required: false,
			default: 750,
		},
		position: {
			required: false,
			type: String,
			default: "top-right",
			validator: isOneOf(DIALOGUE_COMPONENT_POSITIONS),
		},
	},
	data() {
		return {
			isReady: false,
		};
	},
	computed: {
		durationVariables() {
			const segmentDuration = this.duration / 3;
			const vars = {
				"--segment-duration": `${segmentDuration}ms`,
				"--before-element-delay": `${segmentDuration * 0}ms`,
				"--after-element-delay": `${segmentDuration * 1}ms`,
				"--element-delay": `${segmentDuration * 2}ms`,
			};

			return vars;
		},
	},
	methods: {
		ready() {
			this.isReady = true;
			this.$store.dispatch("sound/fx/play", { id: "thought_bubble" });
		},
		unready() {
			this.isReady = false;
			this.$store.dispatch("sound/fx/play", { id: "button_affirmative" });
		},
	},
};
</script>

<style lang="scss" scoped>
	.dialogue-bubble {
		padding: 5% 5% 3%;
		max-width: 30vw;
		min-width: 600px;
		position: relative;
		background: $off-white;
		border-radius: 37% 57% 46% 46% / 43% 48% 49% 51%;
		text-align: center;
		font-family: $body-font-alt;

		@include mobile {
			max-width: 20vw;
			min-width: 300px;
			padding: 6% 4% 3%;
		}

		@include tablet { padding: 6% 6% 3%; }

		&::before { width: 1em; height: 1em; }
		&::after { width: 1.5em; height: 1.5em;}
		&::before, &::after {
			content: "";
			position: absolute;
			background: $off-white;
			border-radius: 93% 75% 100% 66% / 100% 100% 94% 100%;
		}

		&.top-right {
			&::before { left: -.5em; bottom: -.5em; }
			&::after { left: 1em; bottom: 1em; }
		}

		&.top-left {
			&::before { right: -.5em; bottom: -.5em; }
			&::after { right: 1em; bottom: 1em; }
		}

		&.bottom-right {
			&::before { left: -1em; top: -1em; }
			&::after { left: .5em; top: .5em; }
		}

		&.bottom-left {
			&::before { right: -.5em; top: -.5em; }
			&::after { right: 1em; top: 1em; }
		}

	}
	.dialogue-inner {
		opacity: 0;
		transition: .1s;
		&.is-ready { opacity: 1; }
	}


	.dialogue-footer {
		margin-top: 1em;
		display: flex;
		justify-content: center;

		@include mobile {
			flex-direction: column;
			align-items: center;
		}

		& /deep/ .button {
			white-space: nowrap;

			&:not(:first-child) {
				margin-left: 1em;

				@include mobile {
					margin-top: 1em;
				}
			}

			&:first-child {
				@include mobile { margin-right: 1em; }
			}
		}
	}

	.dialogue-fade-enter-active,
	.dialogue-fade-leave-active {
		transition-duration: var(--segment-duration);
		transition-delay: var(--element-delay);

		&, &::before, &::after {
			transition-property: all;
		}

		&::before {
			transition-duration: var(--segment-duration);
			transition-delay: var(--before-element-delay);
		}
		&::after {
			transition-duration: var(--segment-duration);
			transition-delay: var(--after-element-delay);
		}
	}

	.dialogue-fade-enter {
		&, &::before, &::after { background: rgba($off-white, 0); }
	}

	.dialogue-fade-leave-to {
		&, &::before, &::after { background: rgba($off-white, 0); }
	}
</style>



// WEBPACK FOOTER //
// src/components/partials/DialogueBubble.vue